import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "../components/Reveal";
import { FounderCard } from "../components/team/FounderCard";
import { CrewCard } from "../components/team/CrewCard";
import { Seo } from "../components/Seo";
import { useT } from "../i18n/I18nProvider";

const SASHA_IMG =
  "https://customer-assets.emergentagent.com/job_digital-sculptors/artifacts/4jex31nn_Screenshot%202026-04-24%20131301.png";
const RICCARDO_IMG =
  "https://customer-assets.emergentagent.com/job_digital-sculptors/artifacts/u4syixsk_WhatsApp%20Image%202026-04-24%20at%2013.11.34.jpeg";
const FOUNDER_IMAGES = [RICCARDO_IMG, SASHA_IMG];
const CREW_SLUGS = ["illustratori", "animatori", "scultori", "sviluppatori"];

export default function Team() {
  const t = useT();
  const founders = t("team.founders");
  const crew = t("team.crew.items");

  return (
    <>
      <Seo page="team" />
      {/* INTRO */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 pt-16 md:pt-24 pb-16 md:pb-24 grid lg:grid-cols-12 gap-8 md:gap-10 items-end">
        <div className="lg:col-span-8">
          <Reveal>
            <div className="overline mb-4 md:mb-6">{t("team.intro.overline")}</div>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-display text-5xl sm:text-7xl md:text-8xl lg:text-[8.5vw] leading-[0.92] tracking-tightest">
              {t("team.intro.title1")}
              <br />
              <span className="font-editorial text-ink-700">
                {t("team.intro.titleEditorial")}
              </span>
              {t("team.intro.titleEnd")}
            </h1>
          </Reveal>
        </div>
        <div className="lg:col-span-4">
          <Reveal delay={160}>
            <p className="text-[15px] md:text-[16px] text-ink-500 leading-relaxed">
              {t("team.intro.paragraph")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* FOUNDERS */}
      <section
        className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 pb-24 grid md:grid-cols-2 gap-10 md:gap-8 lg:gap-16"
        data-testid="team-founders"
      >
        {founders.map((f, i) => (
          <FounderCard key={f.name} founder={f} index={i} image={FOUNDER_IMAGES[i]} />
        ))}
      </section>

      {/* EXTENDED CREW */}
      <section
        className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 py-16 md:py-32"
        data-testid="team-crew"
      >
        <div className="grid lg:grid-cols-12 gap-6 md:gap-10 mb-10 md:mb-14 items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="overline mb-4 md:mb-5">{t("team.crew.overline")}</div>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tightest leading-[0.95]">
                {t("team.crew.title1")}{" "}
                <span className="font-editorial text-ink-700">
                  {t("team.crew.titleEditorial")}
                </span>
                <br /> {t("team.crew.title2")}
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={140}>
              <p className="text-[14px] md:text-[15px] text-ink-500 leading-relaxed">
                {t("team.crew.paragraph")}
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
          {crew.map((c, i) => (
            <CrewCard key={CREW_SLUGS[i]} item={c} index={i} slug={CREW_SLUGS[i]} />
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 py-16 md:py-24">
        <Reveal className="card-flat p-8 md:p-20 grid lg:grid-cols-12 gap-8 md:gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="overline mb-4 md:mb-5">{t("team.manifesto.overline")}</div>
            <p className="font-editorial text-2xl sm:text-3xl md:text-5xl leading-[1.15] text-ink-900">
              {t("team.manifesto.quote")}
            </p>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link to="/contatti" className="btn-pill" data-testid="team-cta">
              {t("team.manifesto.button")}
              <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.6} />
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
